/**
 * ProjectPicker — landing page after login.
 *
 * Fetches the projects shared with the current user from the backend
 * (`GET /api/projects/`) and renders one card per project.  Cards are
 * sorted closest-to-expiry first so the user sees what's about to vanish.
 *
 * Each card shows:
 *   - thumbnail (placeholder gradient if absent)
 *   - project name + last-updated date
 *   - "Online noch X Tage" countdown derived from `expiresAt`; cards
 *     close to expiry are flagged amber → red.
 */

import { useEffect, useMemo, useState } from 'react'

import { logout } from '../auth/webAuthClient'
import { listProjects, type ProjectSummary } from '../api/projectClient'

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; items: ProjectSummary[] }
  | { kind: 'error'; message: string }

export function ProjectPicker() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    listProjects()
      .then((items) => {
        if (!cancelled) setStatus({ kind: 'ready', items })
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err)
          setStatus({ kind: 'error', message })
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const items = status.kind === 'ready' ? status.items : []
  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        // Closest-to-expiry first so the user sees what's about to vanish.
        const at = a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY
        const bt = b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY
        return at - bt
      }),
    [items],
  )

  return (
    <div style={pageStyle}>
      <div style={blobOneStyle} aria-hidden />
      <div style={blobTwoStyle} aria-hidden />

      <div style={shellStyle}>
        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={logoFrameStyle}>
              <img
                src="/gontec-logo.jpg"
                alt="Gontec-MV"
                draggable={false}
                style={{ width: 40, height: 'auto', borderRadius: 3, display: 'block' }}
              />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: 0.3 }}>3D Lite</h1>
              <div style={{ fontSize: 12, opacity: 0.5, marginTop: 2 }}>
                {status.kind === 'loading'
                  ? 'Lade…'
                  : `${sorted.length} ${sorted.length === 1 ? 'Projekt' : 'Projekte'}`}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              logout()
              window.location.reload()
            }}
            style={signOutStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Abmelden
          </button>
        </header>

        {status.kind === 'loading' ? (
          <div style={emptyStateStyle}>Lade Projekte…</div>
        ) : status.kind === 'error' ? (
          <div style={{ ...emptyStateStyle, borderColor: 'rgba(239,68,68,0.35)', color: '#fca5a5' }}>
            Projekte konnten nicht geladen werden: {status.message}
          </div>
        ) : sorted.length === 0 ? (
          <div style={emptyStateStyle}>
            Noch keine veröffentlichten Projekte. Sobald jemand ein Modell mit
            dir teilt, erscheint es hier.
          </div>
        ) : (
          <div style={gridStyle}>
            {sorted.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Card ───────────────────────────────────────────────────────────────────

function ProjectCard({ project }: { project: ProjectSummary }) {
  const expiry = project.expiresAt ? expiryInfo(project.expiresAt) : null

  return (
    <a
      href={`/p/${project.id}`}
      style={cardStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(13,93,42,0.55)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <div style={thumbStyle(project.thumbnailUrl)}>
        {!project.thumbnailUrl && (
          <div style={thumbPlaceholderStyle}>{project.name.slice(0, 2).toUpperCase()}</div>
        )}
      </div>

      <div style={cardBodyStyle}>
        <div style={cardTitleStyle} title={project.name}>
          {project.name}
        </div>
        <div style={cardMetaStyle}>
          Aktualisiert {formatRelative(project.updatedAt)}
        </div>
        {expiry && (
          <div
            style={{
              ...expiryStyle,
              color: expiry.tone === 'red' ? '#fca5a5' : expiry.tone === 'amber' ? '#fcd34d' : '#9aa1a8',
              background:
                expiry.tone === 'red'
                  ? 'rgba(239,68,68,0.10)'
                  : expiry.tone === 'amber'
                  ? 'rgba(252,211,77,0.08)'
                  : 'rgba(255,255,255,0.04)',
              borderColor:
                expiry.tone === 'red'
                  ? 'rgba(239,68,68,0.35)'
                  : expiry.tone === 'amber'
                  ? 'rgba(252,211,77,0.30)'
                  : 'rgba(255,255,255,0.08)',
            }}
          >
            <span style={dotStyle(expiry.tone)} />
            {expiry.label}
          </div>
        )}
      </div>
    </a>
  )
}

// ─── Time helpers ───────────────────────────────────────────────────────────

function expiryInfo(iso: string): { label: string; tone: 'green' | 'amber' | 'red' } {
  const ms = Date.parse(iso) - Date.now()
  const dayMs = 24 * 3600 * 1000
  const days = Math.ceil(ms / dayMs)
  if (days <= 0) return { label: 'Abgelaufen', tone: 'red' }
  if (days === 1) return { label: 'Online noch 1 Tag', tone: 'red' }
  if (days <= 7) return { label: `Online noch ${days} Tage`, tone: 'amber' }
  return { label: `Online noch ${days} Tage`, tone: 'green' }
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso)
  const dayMs = 24 * 3600 * 1000
  const days = Math.floor(diffMs / dayMs)
  if (days <= 0) return 'heute'
  if (days === 1) return 'gestern'
  if (days < 7) return `vor ${days} Tagen`
  if (days < 30) return `vor ${Math.floor(days / 7)} Wochen`
  return `vor ${Math.floor(days / 30)} Monaten`
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  position: 'relative',
  // The global CSS pins html/body/#root to height:100% with overflow:hidden
  // (so the full-screen 3D viewer never shows scrollbars). That also clips
  // this list, so make the picker its own scroll container — otherwise only
  // the projects that fit one viewport are reachable.
  height: '100vh',
  overflowY: 'auto',
  padding: '2rem 1.25rem',
  background:
    'radial-gradient(1200px 600px at 20% -10%, rgba(13,93,42,0.18), transparent 60%),' +
    'radial-gradient(900px 500px at 100% 110%, rgba(255,212,0,0.06), transparent 55%),' +
    '#0e1013',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  color: '#e7e9ec',
  overflowX: 'hidden',
}

const blobOneStyle: React.CSSProperties = {
  // fixed (not absolute) so it stays a static backdrop and doesn't add to the
  // picker's scroll height now that pageStyle is a scroll container.
  position: 'fixed',
  top: '-160px',
  left: '-120px',
  width: 460,
  height: 460,
  background: 'radial-gradient(circle, rgba(13,93,42,0.35), transparent 65%)',
  filter: 'blur(40px)',
  pointerEvents: 'none',
}

const blobTwoStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: '-200px',
  right: '-160px',
  width: 520,
  height: 520,
  background: 'radial-gradient(circle, rgba(255,212,0,0.10), transparent 65%)',
  filter: 'blur(50px)',
  pointerEvents: 'none',
}

const shellStyle: React.CSSProperties = {
  position: 'relative',
  maxWidth: 1080,
  margin: '0 auto',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '1.75rem',
}

const logoFrameStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 10,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
}

const signOutStyle: React.CSSProperties = {
  padding: '0.45rem 0.9rem',
  background: 'transparent',
  color: '#cfd3d8',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  letterSpacing: 0.3,
  transition: 'background 120ms ease',
}

const emptyStateStyle: React.CSSProperties = {
  padding: '2rem',
  background: 'rgba(20,22,26,0.6)',
  border: '1px dashed rgba(255,255,255,0.1)',
  borderRadius: 12,
  textAlign: 'center',
  color: '#9aa1a8',
  fontSize: 14,
  maxWidth: 560,
  margin: '0 auto',
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '1rem',
}

const cardStyle: React.CSSProperties = {
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  background: 'rgba(20,22,26,0.78)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow:
    '0 10px 30px -16px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
  transition: 'transform 140ms ease, border-color 140ms ease',
  backdropFilter: 'blur(8px) saturate(140%)',
  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
}

const thumbStyle = (url?: string): React.CSSProperties => ({
  width: '100%',
  aspectRatio: '16/9',
  background: url
    ? `linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(14,16,19,0.6) 100%), #111 center/cover no-repeat url(${url})`
    : 'linear-gradient(135deg, rgba(13,93,42,0.20) 0%, rgba(20,22,26,0.6) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
})

const thumbPlaceholderStyle: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  letterSpacing: 1,
  opacity: 0.25,
}

const cardBodyStyle: React.CSSProperties = {
  padding: '0.85rem 0.95rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}

const cardTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const cardMetaStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.5,
  marginBottom: 6,
}

const expiryStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  alignSelf: 'flex-start',
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.3,
  border: '1px solid',
}

const dotStyle = (tone: 'green' | 'amber' | 'red'): React.CSSProperties => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: tone === 'red' ? '#ef4444' : tone === 'amber' ? '#fcd34d' : '#10b981',
  boxShadow: '0 0 6px ' + (tone === 'red' ? '#ef4444' : tone === 'amber' ? '#fcd34d' : '#10b981'),
})
